import Link from 'next/link';
import { date as formatDate, time as formatTime } from '@frameio/i18n';
import { Inline, Stack, Text, VisuallyHidden } from '@frameio/vapor';
import { IconFiletypeFile } from '@frameio/vapor-icons';
import { useQuickSearchProjectFragment } from '../../gql/generated/QuickSearchProject';
import { useNavigateToResult } from '../../hooks';
import { ProjectIcon } from '../shared/TypeIcon';
import { MetadataRow } from './components/MetadataRow';
import {
  DetailContainer,
  DetailPane,
  FileNameAnchor,
  MetaContainer,
  ProjectOrFolderThumbContainer,
  TextWrapper,
  ThumbContainer,
  ThumbnailLinkContainer,
} from './DetailPane';
import TEXT from './generated/TEXT/QuickSearch_ProjectDetailPane';

TEXT.DEFINE({
  /** Label for project */
  project: 'Project',
  /** Label for type */
  type: 'Type',
  /**
   * Inserted date/time in readable format
   * Example (en-US): "Jan 15, 2026 at 2:30 PM"
   */
  insertedDateTime: (insertedTime: Date): string =>
    `${formatDate(insertedTime, 'medium')} at ${formatTime(
      insertedTime,
      'short'
    )}`,
  /**
   * Inserted date/time in full a11y format
   * Example (en-US): "January 15, 2026 at 2:30 PM"
   */
  insertedDateTimeA11y: (insertedTime: Date): string =>
    `${formatDate(insertedTime, 'long')} at ${formatTime(
      insertedTime,
      'short'
    )}`,
});

type Props = {
  resultId: string;
};

export const ProjectDetailPane = ({ resultId }: Props) => {
  const { data: projectResultData } = useQuickSearchProjectFragment({
    from: { __typename: 'Project', id: resultId },
  });
  const { href, navigate } = useNavigateToResult(
    resultId,
    projectResultData.__typename ?? ''
  );
  const projectDetails = projectResultData;

  const isRestrictedProject = projectDetails?.private ?? false;
  const isInactiveProject = Boolean(projectDetails?.archivedAt);
  const insertedAtDate = projectDetails?.insertedAt
    ? new Date(projectDetails.insertedAt)
    : null;

  const handleClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    if (!e.ctrlKey && !e.metaKey) {
      navigate();
      e.preventDefault();
    }
  };

  return (
    <DetailPane onDoubleClick={navigate}>
      <ThumbnailLinkContainer>
        <Link href={href}>
          <span data-testid="detailpane-thumb-link" onClick={handleClick}>
            <ThumbContainer>
              <ProjectOrFolderThumbContainer>
                <ProjectIcon
                  isInactiveProject={isInactiveProject}
                  isRestrictedProject={isRestrictedProject}
                  size={24}
                />
              </ProjectOrFolderThumbContainer>
            </ThumbContainer>
          </span>
        </Link>
      </ThumbnailLinkContainer>
      <DetailContainer>
        <Stack alignX="center" gap={8}>
          <TextWrapper>
            <Link href={href}>
              <FileNameAnchor
                data-testid="detailpane-filename-link"
                onClick={handleClick}
              >
                <Text
                  enableTooltip={true}
                  style={{
                    justifyContent: 'center',
                  }}
                  truncate={{
                    hasExtension: true,
                    type: 'middle',
                    padding: 0,
                  }}
                  variant="header"
                >
                  {projectDetails?.name ?? ''}
                </Text>
              </FileNameAnchor>
            </Link>
          </TextWrapper>
          <Inline
            alignX="center"
            alignY="center"
            gap={4}
            style={{ width: '100%' }}
          >
            <VisuallyHidden>
              {insertedAtDate ? TEXT.insertedDateTimeA11y(insertedAtDate) : ''}
            </VisuallyHidden>
            <Text
              aria-hidden={true}
              color="tertiary"
              textAlign="center"
              variant="secondary"
            >
              {insertedAtDate ? TEXT.insertedDateTime(insertedAtDate) : ''}
            </Text>
          </Inline>
        </Stack>
      </DetailContainer>
      <MetaContainer>
        <MetadataRow
          data-testid="detailpane-filetype"
          icon={IconFiletypeFile}
          metadata={TEXT.project}
          title={TEXT.type}
        />
      </MetaContainer>
    </DetailPane>
  );
};
